import { motion } from "framer-motion";
import { Award, Users, Target, Eye, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] } }),
};

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-16">
      <SEO
        title="About JSY Printing & Packaging"
        description="Learn about JSY Printing & Packaging, founded in Kolkata in 2018, delivering reliable plastic packaging and printing solutions for textile brands."
        image="/images/about-factory-gate.png"
      />
      {/* Hero */}
  <section className="py-12 px-6 bg-[#F4F5F7]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="overline">About JSY</span>
            <h1 className="mt-3 text-5xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>
              Crafting Packaging & Printing<br />Excellence Since <span className="text-[#1D4ED8]">2018</span>
            </h1>
            <p className="mt-4 text-[#52525B] text-sm md:text-base tracking-wide">
              We deliver excellence with simplicity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="overline">Our Story</span>
            <h2 className="mt-3 text-3xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>
              From Vision to Industry Leader
            </h2>
            <p className="mt-5 text-[#71717A] leading-relaxed">
              Founded in 2018 in Kolkata by <strong>Mr. Sanjib Kumar Saha</strong>, JSY began with a clear mission: to provide the textile industry with packaging and printing solutions that combine reliability, quality, and innovation. Starting from a single production line, we have grown into a full-scale manufacturing facility equipped with cutting-edge machinery at our Maheshtala plant.
            </p>
            <p className="mt-4 text-[#71717A] leading-relaxed">
              Today, we serve over 500 clients across India and international markets, producing more than 10 million packaging units monthly. Our commitment to quality and on-time delivery has made us a trusted partner for some of the most respected names in the textile sector.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { val: "2018", label: "Year Founded" },
                { val: "500+", label: "Active Clients" },
                { val: "10M+", label: "Monthly Output" },
                { val: "15+", label: "Export Markets" },
              ].map((s) => (
                <div key={s.label} className="stat-card rounded">
                  <p className="text-2xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>{s.val}</p>
                  <p className="text-xs text-[#71717A] mt-1 tracking-wide uppercase">{s.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded overflow-hidden border border-[#E4E4E7]"
          >
            <img
              src="/images/about-factory-gate.png"
              alt="JSY Factory"
              className="w-full h-full object-cover"
              style={{ minHeight: '400px' }}
            />
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
  <section className="py-12 px-6 bg-[#F4F5F7]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="bento-card p-10 bg-white">
              <div className="w-12 h-12 rounded flex items-center justify-center bg-[#EBF0FF] text-[#1D4ED8] mb-5">
                <Target size={24} />
              </div>
              <h3 className="text-xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>Our Mission</h3>
              <p className="mt-3 text-[#71717A] leading-relaxed">
                To deliver world-class plastic packaging and printing solutions that protect, preserve, and promote our clients' products. We strive for manufacturing excellence through continuous innovation, sustainable practices, and unwavering commitment to quality.
              </p>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp} className="bento-card p-10 bg-white">
              <div className="w-12 h-12 rounded flex items-center justify-center bg-[#F0FDF4] text-[#16A34A] mb-5">
                <Eye size={24} />
              </div>
              <h3 className="text-xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>Our Vision</h3>
              <p className="mt-3 text-[#71717A] leading-relaxed">
                To become the most trusted name in packaging and printing manufacturing across South Asia, recognized for innovation, reliability, and exceptional customer service. We envision expanding our export footprint while maintaining the personalized attention that sets us apart.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
  <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-8">
            <span className="overline">Our Values</span>
            <h2 className="mt-3 text-3xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>
              What Drives Us
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: <Award size={20} />, title: "Quality First", desc: "Every product undergoes rigorous testing before leaving our facility. We never compromise on quality." },
              { icon: <Users size={20} />, title: "Customer Centric", desc: "Your success is our success. We work closely with each client to understand and exceed their expectations." },
              { icon: <CheckCircle size={20} />, title: "Continuous Improvement", desc: "We invest in the latest technology and training to stay ahead of industry trends and deliver better results." },
            ].map((v, i) => (
              <motion.div key={v.title} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp} className="bento-card p-8 bg-white">
                <div className="text-[#1D4ED8] mb-4">{v.icon}</div>
                <h3 className="text-base tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>{v.title}</h3>
                <p className="text-sm text-[#71717A] leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
  <section className="py-12 px-6 bg-[#09090B] text-white text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className="text-3xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>
            Ready to Partner with JSY?
          </h2>
          <p className="mt-3 text-[#A1A1AA]">Let's discuss how we can support your packaging and printing needs.</p>
          <Link to="/contact" className="btn-primary mt-6 inline-flex">Get in Touch</Link>
        </motion.div>
      </section>
    </div>
  );
}
